// Copyright (c) 2026 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import { DateTime } from "luxon";
import { TimeUnit } from "chart.js";

import { Aggregation } from "graphql/api";
import { PeriodIdentfier, TimePeriod } from "types";
import { isSet } from "shared/utils/utils";
import { IState } from "states/states";
import { getDeletionBoundary, getGraphBoundaryDatesFromPeriod } from "guiutils";

// time range and aggregation of trend data to load
export interface ITrendWindow {
  startDate: number | undefined; // s, undefined: unbounded
  endDate: number | undefined; // s, exclusive, undefined: unbounded
  aggregation: Aggregation;
  // raw data following the present: new periods get appended, also after endDate passed
  isLive: boolean;
}

// padding added around a zoomed range, s
const ZOOM_PADDING: Record<Aggregation, number> = {
  [Aggregation.NONE]: 3600,
  [Aggregation.HOUR]: 3600,
  [Aggregation.DAY]: 24 * 3600,
  [Aggregation.MONTH]: 31 * 24 * 3600,
};

class TrendWindowModel {
  // start of data that is only accessible as daily aggregates (licensing), s
  public static getAggregationBoundary(state: IState) {
    return getDeletionBoundary(state).aggregationBoundary?.toSeconds();
  }

  // aggregation used to load and show data of a time range
  // fixed periods map directly (guireducer already switches old DAY/WEEK periods to MONTH),
  // custom ranges (and zooming) depend on their length and the aggregation boundary
  public static getAggregation(
    timePeriod: TimePeriod,
    startDate: number | undefined,
    endDate: number | undefined,
    aggregationBoundary: number | undefined,
  ) {
    if (timePeriod !== TimePeriod.CUSTOM || !isSet(startDate)) {
      return (
        timePeriod === TimePeriod.DAY ? Aggregation.NONE
        : timePeriod === TimePeriod.WEEK ? Aggregation.HOUR
        : timePeriod === TimePeriod.MONTH ? Aggregation.DAY
        : /* zoom === TimePeriod.YEAR */ Aggregation.MONTH
      );
    }

    // endDate is the exclusive end of the range, so each boundary below is still the lower
    // aggregation (e.g. end === start + 7 days is exactly one week -> HOUR, not DAY).
    // Comparing calendar units rather than elapsed milliseconds keeps this correct across DST.
    const start = DateTime.fromSeconds(startDate);
    const end = isSet(endDate) ? DateTime.fromSeconds(endDate) : DateTime.now();

    if (end > start.plus({ months: 3 })) return Aggregation.MONTH;
    if (end > start.plus({ days: 7 })) return Aggregation.DAY;
    // old data is only accessible as daily aggregates
    if (isSet(aggregationBoundary) && startDate < aggregationBoundary) return Aggregation.DAY;
    if (end > start.plus({ days: 1 })) return Aggregation.HOUR;

    return Aggregation.NONE;
  }

  // time range of the available data shown with the given period, s
  public static getDataBoundaries(state: IState, identifier: PeriodIdentfier) {
    switch (identifier) {
      case "ioPeriod":
        return { min: state.trending.minIoTimestamp, max: state.trending.maxIoTimestamp };
      case "chargingSessionFilter":
        return { min: state.ocpp.minSessionTimestamp, max: state.ocpp.maxSessionTimestamp };
      case "trendPeriod":
        return { min: state.trending.minTrendTimestamp, max: state.trending.maxTrendTimestamp };
    }
  }

  // aggregation of the IO trend; it is loaded for the selected period (no reload on zoom)
  public static getIoAggregation(state: IState) {
    const { timePeriod, graphStart, graphEnd } = getGraphBoundaryDatesFromPeriod(
      state.gui.ioPeriod,
      state.site.tzOffset,
    );
    return this.getAggregation(
      timePeriod,
      graphStart,
      graphEnd,
      this.getAggregationBoundary(state),
    );
  }

  public static getTimeUnit(aggregation: Aggregation): TimeUnit {
    switch (aggregation) {
      case Aggregation.MONTH:
        return "month";
      case Aggregation.DAY:
        return "day";
      case Aggregation.HOUR:
        return "hour";
      case Aggregation.NONE:
        return "minute";
      default:
        throw new Error(`unexpected aggregation: ${aggregation}`);
    }
  }

  // derive the energy trend window of the selected period; if the zoomed range exceeds the
  // period or requires a finer aggregation, we take the (padded) zoom range instead
  public static fromPeriod(state: IState, withZoom: boolean): ITrendWindow {
    const period = state.gui.trendPeriod;
    const { timePeriod, graphStart, graphEnd } = getGraphBoundaryDatesFromPeriod(
      period,
      state.site.tzOffset,
    );
    const aggregationBoundary = this.getAggregationBoundary(state);
    const periodAggregation = this.getAggregation(
      timePeriod,
      graphStart,
      graphEnd,
      aggregationBoundary,
    );
    const periodWindow: ITrendWindow = {
      startDate: graphStart,
      endDate: graphEnd,
      aggregation: periodAggregation,
      isLive: this.reachesNow(periodAggregation, graphEnd),
    };
    if (!withZoom || !isSet(period.zoomMin) || !isSet(period.zoomMax)) {
      return periodWindow;
    }

    const zoomStart = period.zoomMin / 1000; // ms -> s
    const zoomEnd = period.zoomMax / 1000;
    const aggregation = this.getAggregation(
      TimePeriod.CUSTOM,
      zoomStart,
      zoomEnd,
      aggregationBoundary,
    );
    if (this.covers(periodWindow, { startDate: zoomStart, endDate: zoomEnd, aggregation })) {
      return periodWindow;
    }
    const endDate = Math.ceil(zoomEnd + ZOOM_PADDING[aggregation]);
    return {
      startDate: Math.floor(zoomStart - ZOOM_PADDING[aggregation]),
      endDate,
      aggregation,
      isLive: this.reachesNow(aggregation, endDate),
    };
  }

  // a raw data window reaching up to now at load time follows the present
  private static reachesNow(aggregation: Aggregation, endDate: number | undefined) {
    return aggregation === Aggregation.NONE && (!isSet(endDate) || endDate * 1000 > Date.now());
  }

  // true if the loaded window provides all data of the wanted one
  public static covers(loaded: ITrendWindow | undefined, wanted: Omit<ITrendWindow, "isLive">) {
    return (
      isSet(loaded) &&
      loaded.aggregation === wanted.aggregation &&
      (!isSet(loaded.startDate) ||
        (isSet(wanted.startDate) && wanted.startDate >= loaded.startDate)) &&
      (!isSet(loaded.endDate) || (isSet(wanted.endDate) && wanted.endDate <= loaded.endDate))
    );
  }
}

export default TrendWindowModel;
